import { useMemo, useState } from "react";
import { Link } from "react-router";
import { DailyLines, RangePicker, Segmented, ShareBars } from "../components/charts";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Mono,
  PageHeader,
  Table,
  Td,
  Th,
  Tag,
  cx,
} from "../components/ui";
import { formatNumber } from "../lib/format";
import { money, moneyCompact } from "../lib/money";
import { useInferenceMargin, useInferenceSeries } from "../lib/queries";
import type {
  InferenceMargin,
  InferenceMarginBreakdown,
  InferenceMarginModel,
  InferenceMarginOrg,
} from "../lib/types";

/**
 * Whether inference is being sold above what it costs.
 *
 * Every other AI screen in this console is about the catalogue — what exists,
 * what it is priced at, what is listed. This one is about what actually
 * happened, and it exists because the platform has been recording the answer for
 * two migrations and showing it to nobody: `upstream_cost_picounits` since 0046,
 * the prompt-cache counts since 0059. Reading either meant a hand-written query
 * or grepping `inference_billed_under_cost` out of the logs, which nothing
 * alerts on.
 *
 * The page is arranged around one distinction, and getting it wrong is the way
 * this screen would lie:
 *
 *   **Charged covers every request. Cost covers the ones the supplier priced.**
 *
 * Those are different row sets. The supplier reports a cost on most requests and
 * not all, so dividing the full charged total by a partial cost total would
 * report a margin several times the real one — and would look *better* the more
 * data was missing, which is the worst possible direction for a number an
 * operator uses to decide whether to raise a price. So the margin figure is
 * computed on the measured subset alone, and the size of that subset is printed
 * beside it rather than left to be assumed.
 */
export function AiUsagePage() {
  const [days, setDays] = useState(30);
  const { data, isPending, error, isFetching } = useInferenceMargin(days);
  const series = useInferenceSeries(days);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const c = data.currency;
  const t = data.totals;
  const coverage = share(t.measuredRequests, t.requests);
  /*
   * Campaign traffic, which every figure in `totals` now excludes — it has no
   * margin, because nobody paid for it. Kept separate rather than folded in,
   * and checked by the empty state below: a window whose only inference was
   * given away has `t.requests === 0` and is not an empty window.
   */
  const gift = data.givenAway;

  return (
    <>
      <PageHeader
        title="AI usage"
        description="What we charged, what it cost us, and where the two do not match."
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <RangePicker ranges={data.ranges} value={days} onChange={setDays} />
        <Link
          to="/ai-models"
          className="text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
        >
          Edit prices in AI models
        </Link>
      </div>

      {t.requests === 0 && gift.requests === 0 ? (
        <Card>
          <EmptyState
            icon="models"
            title={`No inference in the last ${days} days`}
            description="Nothing was called. Try a wider range."
          />
        </Card>
      ) : (
        <>
          <section
            className={cx(
              "grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-4",
              // Same reasoning as the tenant strip: a figure from the previous
              // range that still looks live is worse than a gap.
              isFetching && "opacity-50 transition-opacity duration-200",
            )}
          >
            <Figure
              label="Charged"
              value={moneyCompact(t.chargedMicros, c)}
              detail={`${formatNumber(t.requests)} requests · ${formatNumber(t.organizations)} orgs`}
              note={
                gift.requests === 0
                  ? "Every request in the window."
                  : "Paid requests only — given away is below."
              }
              tone="neutral"
            />
            <Figure
              label="Supplier cost"
              value={moneyCompact(t.measuredUpstreamMicros, c)}
              detail={`on ${formatNumber(t.measuredRequests)} priced requests`}
              note={
                coverage === null
                  ? "The supplier priced none of these."
                  : `${coverage}% of requests carry a cost.`
              }
              tone={coverage !== null && coverage < 80 ? "warn" : "neutral"}
            />
            <Figure
              label="Margin over cost"
              value={t.marginBps === null ? "—" : formatBps(t.marginBps)}
              /*
                Charged is restated here rather than pointing at the first tile,
                because it is a *different figure* — the charge on the measured
                subset, not on everything. Two tiles labelled "charged" showing
                different numbers is confusing; one of them saying which requests
                it covers is not.
              */
              detail={
                t.marginBps === null
                  ? "nothing measured"
                  : `${moneyCompact(t.measuredChargedMicros, c)} charged on those`
              }
              note="Over cost, not over revenue. Same as AI models."
              /* The same three bands the model form uses for its margin figure:
                 below cost is a loss, under 500bp is thin, above is fine. A
                 second colour scale for the same quantity would make the two
                 screens disagree about what a healthy margin looks like. */
              tone={marginTone(t.marginBps)}
            />
            <Figure
              label="Billed under cost"
              value={formatNumber(t.underCostRequests)}
              detail={
                t.underCostRequests === 0
                  ? "no requests lost money"
                  : `${money(t.underCostMicros, c)} short`
              }
              note="We billed less than the supplier charged us."
              tone={t.underCostRequests > 0 ? "bad" : "good"}
            />
          </section>

          {/*
            Given away, on its own strip and only when there is any.

            Below the margin tiles rather than among them, because it is not a
            margin: nobody paid, so there is no revenue to divide by cost. What
            it has is a supplier invoice, and leaving campaign traffic out of
            the figures above without showing it anywhere would trade one wrong
            number for a blind spot.

            The two money figures are deliberately both here. Supplier cost is
            what left the business. Drawn from pools is the same traffic at the
            sell rate — what the campaign budgets actually spent, and the figure
            the campaign console reconciles against. They differ by the markup,
            and an operator asking "what did this cost us" and one asking "how
            much budget is gone" want different ones.
          */}
          {gift.requests > 0 && (
            <section
              className={cx(
                "grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-3",
                isFetching && "opacity-50 transition-opacity duration-200",
              )}
            >
              <Figure
                label="Given away"
                value={formatNumber(gift.requests)}
                detail={`${formatNumber(gift.organizations)} orgs on campaign keys`}
                note="Free inference. Excluded from every figure above."
                tone="neutral"
              />
              <Figure
                label="Cost of the giveaway"
                value={moneyCompact(gift.upstreamMicros, c)}
                detail={`on ${formatNumber(gift.measuredRequests)} priced requests`}
                note="What the supplier charged. Real money, no revenue against it."
                tone={gift.upstreamMicros > 0 ? "warn" : "neutral"}
              />
              <Figure
                label="Drawn from pools"
                value={moneyCompact(gift.drawnMicros, c)}
                detail={`${formatNumber(gift.inputTokens + gift.outputTokens)} tokens`}
                note="At the sell rate — what campaign budgets spent."
                tone="neutral"
              />
            </section>
          )}

          {/*
            The daily line the totals above compress into one figure. Charged
            and supplier cost are drawn against each other, and the days the
            supplier charged more than we billed are ticked in red — a healthy
            window total can hide one bad day, and this is where that day is.
          */}
          <TrendCard days={days} series={series} currency={c} dimmed={isFetching} />

          {/*
            What the charge is actually made of, which the platform has been
            recording since 0059 and showing nobody. Two halves of one figure,
            and the split is the whole point: output is normally most of the
            money on a chat workload, so a margin problem on a model with heavy
            output is a different fix from one with heavy input — the first is a
            rate-card problem, the second is usually a caching one.
          */}
          <CompositionCard totals={t} currency={c} dimmed={isFetching} />

          {/*
            The two lines that turn this page from a readout into a decision.
            Cache reads earn more than the rate card assumes and cache writes
            earn less, and neither was attributable to anything before the counts
            existed — a shortfall could be seen and not explained.
          */}
          <CachePanel totals={t} currency={c} days={days} />

          <BreakdownCard data={data} currency={c} />

          <p className="text-xs leading-6 text-fg-subtle">
            Margin is over supplier cost, like the column in AI models: 500bp reads 5%, which is
            4.76% of what the customer pays. Charged includes usage not yet billed, so it can run up
            to an hour ahead of{" "}
            <Link to="/revenue" className="text-accent underline underline-offset-2">
              Revenue
            </Link>
            .
          </p>
        </>
      )}
    </>
  );
}

/**
 * Which half of a request the money came from.
 *
 * Both figures have been recorded since migration 0059 and displayed nowhere.
 * They matter because the two halves fail differently: output is normally most
 * of the charge on a chat workload, so a thin margin on an output-heavy model is
 * a rate-card problem, while a thin one on an input-heavy model is usually a
 * caching problem — and the fix for each is in a different place.
 *
 * The split covers rate-carded requests only. A cost-priced model is charged
 * from what the request cost as a whole and has no per-half rate to attribute,
 * so it contributes to neither side; the panel says what fraction of the charge
 * it is describing rather than implying it covers everything.
 */
function CompositionCard({
  totals,
  currency,
  dimmed,
}: {
  totals: InferenceMarginBreakdown;
  currency: string;
  dimmed: boolean;
}) {
  const attributed = totals.inputCostMicros + totals.outputCostMicros;
  const coverage = share(attributed, totals.chargedMicros);
  const reasoning = share(totals.reasoningTokens, totals.outputTokens);

  // Nothing rate-carded in the window: an all-video or all-image range is a
  // legitimate state, and an empty pair of bars would read as a broken panel.
  if (attributed <= 0) return null;

  return (
    <Card>
      <CardHeader
        title="What was charged for"
        description="The two halves of a request, and the tokens nobody sees."
      />
      <div className={cx("grid gap-px bg-line sm:grid-cols-2", dimmed && "opacity-50 transition-opacity duration-200")}>
        <div className="bg-surface px-5 py-4">
          <p className="micro">Input against output</p>
          <p className="mt-1 mb-4 text-xs text-fg-subtle">
            {coverage === null || coverage >= 100
              ? "Every request here."
              : `${coverage}% of the charge. The rest is cost-priced models, which have no input or output rate.`}
          </p>
          <ShareBars
            total={attributed}
            shares={[
              { label: "Input", value: totals.inputCostMicros },
              { label: "Output", value: totals.outputCostMicros },
            ]}
            format={(value) => money(value, currency)}
          />
        </div>
        <div className="bg-surface px-5 py-4">
          <p className="micro">Spent reasoning</p>
          <p className="mt-1.5 font-mono text-2xl tabular-nums text-fg">
            {formatNumber(totals.reasoningTokens)}
          </p>
          <p className="mt-1 text-xs leading-5 text-fg-subtle">
            {reasoning === null ? "—" : `${reasoning}% of output tokens`} · billed as output, which
            is what the supplier charges for them. Part of the output count above, not extra. The
            customer pays for these and never sees them, which is why reasoning models cost more.
          </p>
        </div>
      </div>
    </Card>
  );
}

/**
 * The same window, cut three ways.
 *
 * One table with a grouping switch rather than three stacked tables, because
 * the columns are identical and the question is not — "which model is
 * unprofitable" and "which tenant is unprofitable" have different answers and
 * different responses. A model losing money across four hundred tenants is a
 * pricing error; the same model losing money for one tenant is that tenant's
 * workload, and repricing the catalogue over it would be the wrong move.
 */
type Grouping = "model" | "tenant" | "publisher";

const GROUPINGS: { value: Grouping; label: string }[] = [
  { value: "model", label: "Model" },
  { value: "tenant", label: "Tenant" },
  { value: "publisher", label: "Publisher" },
];

function BreakdownCard({ data, currency }: { data: InferenceMargin; currency: string }) {
  const [grouping, setGrouping] = useState<Grouping>("model");
  const publishers = useMemo(() => rollUpByPublisher(data.byModel), [data.byModel]);

  const description: Record<Grouping, string> = {
    model: "Worst margin first. Models with no supplier cost sort last — a blind spot is not a loss.",
    tenant: "Biggest spender first, because the worst margin here is usually someone who spent four cents.",
    publisher: "Models added up by publisher. Margin is recalculated from the totals, not averaged.",
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Breakdown"
          description={description[grouping]}
          action={
            <Segmented
              options={GROUPINGS}
              value={grouping}
              onChange={setGrouping}
              label="Group by"
            />
          }
        />

        {grouping === "model" &&
          (data.byModel.length === 0 ? (
            <Empty />
          ) : (
            <Table
              head={
                <tr>
                  <Th>Model</Th>
                  <Th priority="lg">Basis</Th>
                  <Th className="text-right">Requests</Th>
                  <Th className="text-right">Charged</Th>
                  <Th priority="md" className="text-right">
                    Cost
                  </Th>
                  <Th className="text-right">Margin</Th>
                  <Th priority="lg" className="text-right">
                    Cached in
                  </Th>
                </tr>
              }
            >
              {data.byModel.map((row) => (
                <ModelRow key={row.model} row={row} currency={currency} />
              ))}
            </Table>
          ))}

        {grouping === "tenant" &&
          (data.byOrganization.length === 0 ? (
            <Empty />
          ) : (
            <Table
              head={
                <tr>
                  <Th>Organization</Th>
                  <Th className="text-right">Requests</Th>
                  <Th className="text-right">Charged</Th>
                  <Th priority="md" className="text-right">
                    Cost
                  </Th>
                  <Th className="text-right">Margin</Th>
                </tr>
              }
            >
              {data.byOrganization.map((row) => (
                <OrgRow key={row.orgId} row={row} currency={currency} />
              ))}
            </Table>
          ))}

        {grouping === "publisher" &&
          (publishers.length === 0 ? (
            <Empty />
          ) : (
            <Table
              head={
                <tr>
                  <Th>Publisher</Th>
                  <Th priority="lg" className="text-right">
                    Models
                  </Th>
                  <Th className="text-right">Requests</Th>
                  <Th className="text-right">Charged</Th>
                  <Th priority="md" className="text-right">
                    Cost
                  </Th>
                  <Th className="text-right">Margin</Th>
                </tr>
              }
            >
              {publishers.map((row) => (
                <PublisherTableRow key={row.publisher} row={row} currency={currency} />
              ))}
            </Table>
          ))}
      </Card>

      {/* Stated rather than silently truncated, and per grouping, because a
          publisher rollup built from a capped model list is capped too. */}
      {grouping !== "tenant" && data.byModel.length >= data.modelLimit && (
        <Alert tone="warning">
          {grouping === "model"
            ? `Stops at ${data.modelLimit} models. Narrow the range to see fewer.`
            : `Added up from the first ${data.modelLimit} models only, so a publisher below that cut is missing here.`}
        </Alert>
      )}
      {grouping === "tenant" && data.byOrganization.length >= data.orgLimit && (
        <Alert tone="warning">
          Stops at the top {data.orgLimit} tenants by spend. Anyone below that is not shown.
        </Alert>
      )}
    </>
  );
}

function Empty() {
  return <p className="px-4 py-6 text-sm text-fg-subtle">Nothing in this window.</p>;
}

export interface PublisherRow {
  publisher: string;
  models: number;
  requests: number;
  chargedMicros: number;
  measuredRequests: number;
  measuredChargedMicros: number;
  measuredUpstreamMicros: number;
  underCostRequests: number;
  marginBps: number | null;
}

/**
 * Models summed by the name in front of the slash.
 *
 * Done here rather than in SQL because every input is already on the page and a
 * second query would be a second definition of margin to keep in step. The
 * ordering is by spend, matching the tenant view — a publisher is read to find
 * out who the platform depends on.
 */
export function rollUpByPublisher(models: InferenceMarginModel[]): PublisherRow[] {
  const map = new Map<string, PublisherRow>();

  for (const model of models) {
    const row = map.get(model.publisher) ?? {
      publisher: model.publisher,
      models: 0,
      requests: 0,
      chargedMicros: 0,
      measuredRequests: 0,
      measuredChargedMicros: 0,
      measuredUpstreamMicros: 0,
      underCostRequests: 0,
      marginBps: null,
    };

    row.models += 1;
    row.requests += model.requests;
    row.chargedMicros += model.chargedMicros;
    row.measuredRequests += model.measuredRequests;
    row.measuredChargedMicros += model.measuredChargedMicros;
    row.measuredUpstreamMicros += model.measuredUpstreamMicros;
    row.underCostRequests += model.underCostRequests;
    map.set(model.publisher, row);
  }

  return [...map.values()]
    .map((row) => ({
      ...row,
      /*
       * Recomputed from the summed measured figures, never averaged across the
       * model rows. An average of percentages weights a model with four
       * requests the same as one with four hundred thousand, which is exactly
       * how a rollup reports a comfortable margin for a publisher that is
       * losing money — the same full-over-partial mistake the totals guard
       * against, one level up.
       */
      marginBps:
        row.measuredUpstreamMicros > 0
          ? Math.round(
              ((row.measuredChargedMicros - row.measuredUpstreamMicros) /
                row.measuredUpstreamMicros) *
                10000,
            )
          : null,
    }))
    .sort((a, b) => b.chargedMicros - a.chargedMicros);
}

/**
 * What the prompt cache is doing to the margin.
 *
 * Its own panel rather than two more tiles, because the two counts are read
 * against each other and against the input total they are part of — and because
 * the reading is a recommendation rather than a measurement. Reads are billed
 * above cost and writes below it, so a model whose traffic is mostly writes is
 * losing money in a way the headline margin can absorb without showing.
 */
function CachePanel({
  totals,
  currency,
  days,
}: {
  totals: InferenceMarginBreakdown;
  currency: string;
  days: number;
}) {
  const reported = share(totals.cacheReportedRequests, totals.requests);

  if (totals.cachedInputTokens === 0 && totals.cacheWriteTokens === 0) {
    return (
      <Card>
        <CardHeader
          title="Prompt cache"
          description={`No provider reported cache activity in the last ${days} days.`}
        />
        <p className="px-4 pb-5 text-xs leading-6 text-fg-subtle">
          {reported === null || reported === 0
            ? "No request reported cache detail at all, so this is silence rather than a cold cache. Most providers report it only on models that support caching."
            : `${reported}% of requests reported cache detail, and none of them cached anything.`}
        </p>
      </Card>
    );
  }

  const readShare = share(totals.cachedInputTokens, totals.inputTokens);
  const writeShare = share(totals.cacheWriteTokens, totals.inputTokens);

  return (
    <Card>
      <CardHeader
        title="Prompt cache"
        description="Both counts are part of the input tokens, not extra."
      />
      <div className="grid gap-px bg-line sm:grid-cols-2">
        <div className="bg-surface px-4 py-4">
          <p className="micro">Read from cache</p>
          <p className="mt-1.5 font-mono text-2xl tabular-nums text-fg">
            {formatNumber(totals.cachedInputTokens)}
          </p>
          <p className="mt-1 text-xs text-fg-subtle">
            {readShare === null ? "—" : `${readShare}% of input tokens`} · billed at the
            model&rsquo;s cached rate, where it publishes one. Cheaper for the customer, and we
            still make our margin.
          </p>
        </div>
        <div className="bg-surface px-4 py-4">
          <p className="micro">Written to cache</p>
          <p className="mt-1.5 font-mono text-2xl tabular-nums text-fg">
            {formatNumber(totals.cacheWriteTokens)}
          </p>
          <p className="mt-1 text-xs text-fg-subtle">
            {writeShare === null ? "—" : `${writeShare}% of input tokens`} · billed at the
            model&rsquo;s cache-write rate. Above the input rate on Anthropic and OpenAI, below it
            on several Google models. The charge follows the supplier either way.
          </p>
        </div>
      </div>
      <p className="border-t border-line px-4 py-3 text-xs leading-6 text-fg-subtle">
        {totals.cachedSavingMicros > 0 ? (
          <>
            <span className="text-mint">
              {money(totals.cachedSavingMicros, currency)} discounted
            </span>{" "}
            to customers on cache hits. Revenue we chose not to collect, and already left out of
            Charged above.{" "}
          </>
        ) : (
          <>
            No cache discount here: these models publish no cached rate, so hits were billed at the
            plain input rate.{" "}
          </>
        )}
        {reported === null ? null : <>Cache detail reported on {reported}% of requests. </>}
        The write premium is <em>inside</em> Charged, unlike the discount, because the customer paid
        it. One gap remains: a request mixing five-minute and one-hour{" "}
        <Mono className="text-[11px]">cache_control</Mono> blocks bills every write at the base rate.
        See OPERATIONS.md.
      </p>
    </Card>
  );
}

/**
 * The daily charged-versus-cost chart.
 *
 * Two lines, never one margin line: a single line dividing the two would report
 * a margin over days where the supplier priced nothing, which is the same
 * full-charged-over-partial-cost mistake the totals guard against. The lines
 * let the eye subtract, and the flags name the days that lost money outright.
 */
function TrendCard({
  days,
  series,
  currency,
  dimmed,
}: {
  days: number;
  series: ReturnType<typeof useInferenceSeries>;
  currency: string;
  /** The tiles above dim while a new range loads; the chart follows them. */
  dimmed: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="By day"
        description="Charged against supplier cost, day by day."
      />
      {series.isPending ? (
        <LoadingBlock />
      ) : series.error ? (
        <div className="px-4 pb-4">
          <Alert>{series.error.message}</Alert>
        </div>
      ) : (
        <div className={cx("px-4 pb-4", dimmed && "opacity-50 transition-opacity duration-200")}>
          <DailyLines
            days={series.data.days.map((day) => day.day)}
            series={[
              {
                label: "Charged",
                values: series.data.days.map((day) => day.chargedMicros),
                tone: "primary",
              },
              {
                label: "Supplier cost",
                values: series.data.days.map((day) =>
                  day.measuredRequests === 0 ? null : day.measuredUpstreamMicros,
                ),
                tone: "muted",
              },
            ]}
            flags={series.data.days.map((day) => day.underCostRequests > 0)}
            flagsLabel="A red tick marks a day with requests billed under cost."
            flagsPhrase="billed under cost"
            format={(value) => money(value, currency)}
            label="Daily inference charged and supplier cost"
            windowLabel={`over the last ${days} days`}
          />
        </div>
      )}
    </Card>
  );
}

function ModelRow({ row, currency }: { row: InferenceMarginModel; currency: string }) {
  return (
    <tr>
      <Td>
        <Mono className="text-xs">{row.model}</Mono>
        {row.billingMode === null && (
          // Usage outlives the catalogue row on purpose: `inference_usage.model`
          // is text so a retired model's history still resolves.
          <span className="ml-2 text-[11px] text-fg-subtle">retired</span>
        )}
        {row.underCostRequests > 0 && (
          <span className="ml-2 text-[11px] text-danger">
            {formatNumber(row.underCostRequests)} under cost
          </span>
        )}
      </Td>
      <Td priority="lg">
        {row.billingMode === "upstream_cost" ? (
          <Tag tone="neutral">per request</Tag>
        ) : (
          <span className="text-xs text-fg-subtle">rate card</span>
        )}
      </Td>
      <Td className="text-right font-mono text-xs tabular-nums">{formatNumber(row.requests)}</Td>
      <Td className="text-right font-mono text-xs tabular-nums">
        {money(row.chargedMicros, currency)}
      </Td>
      <Td priority="md" className="text-right font-mono text-xs tabular-nums">
        {row.measuredRequests === 0 ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          money(row.measuredUpstreamMicros, currency)
        )}
      </Td>
      <MarginCell
        marginBps={row.marginBps}
        measuredRequests={row.measuredRequests}
        requests={row.requests}
      />
      <Td priority="lg" className="text-right font-mono text-xs tabular-nums text-fg-subtle">
        {row.cacheReportedRequests === 0 ? "—" : formatNumber(row.cachedInputTokens)}
      </Td>
    </tr>
  );
}

function OrgRow({ row, currency }: { row: InferenceMarginOrg; currency: string }) {
  return (
    <tr className="hover:bg-raised">
      <Td>
        <Link
          to={`/organizations/${row.orgId}`}
          className="font-medium text-accent hover:underline"
        >
          {row.orgName}
        </Link>
        {row.underCostRequests > 0 && (
          <span className="ml-2 text-[11px] text-danger">
            {formatNumber(row.underCostRequests)} under cost
          </span>
        )}
      </Td>
      <Td className="text-right font-mono text-xs tabular-nums">{formatNumber(row.requests)}</Td>
      <Td className="text-right font-mono text-xs tabular-nums">
        {money(row.chargedMicros, currency)}
      </Td>
      <Td priority="md" className="text-right font-mono text-xs tabular-nums">
        {row.measuredRequests === 0 ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          money(row.measuredUpstreamMicros, currency)
        )}
      </Td>
      <MarginCell
        marginBps={row.marginBps}
        measuredRequests={row.measuredRequests}
        requests={row.requests}
      />
    </tr>
  );
}

function PublisherTableRow({ row, currency }: { row: PublisherRow; currency: string }) {
  return (
    <tr className="hover:bg-raised">
      <Td>
        <Mono className="text-xs">{row.publisher}</Mono>
        {row.underCostRequests > 0 && (
          <span className="ml-2 text-[11px] text-danger">
            {formatNumber(row.underCostRequests)} under cost
          </span>
        )}
      </Td>
      <Td priority="lg" className="text-right font-mono text-xs tabular-nums text-fg-subtle">
        {formatNumber(row.models)}
      </Td>
      <Td className="text-right font-mono text-xs tabular-nums">{formatNumber(row.requests)}</Td>
      <Td className="text-right font-mono text-xs tabular-nums">
        {money(row.chargedMicros, currency)}
      </Td>
      <Td priority="md" className="text-right font-mono text-xs tabular-nums">
        {row.measuredRequests === 0 ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          money(row.measuredUpstreamMicros, currency)
        )}
      </Td>
      <MarginCell
        marginBps={row.marginBps}
        measuredRequests={row.measuredRequests}
        requests={row.requests}
      />
    </tr>
  );
}

/**
 * The margin cell, shared by every grouping.
 *
 * One implementation because the colour bands and the measured-subset
 * annotation have to mean the same thing in all three views. Three copies would
 * drift, and a figure reading amber under Model and plain under Publisher
 * teaches an operator to trust neither.
 */
function MarginCell({
  marginBps,
  measuredRequests,
  requests,
}: {
  marginBps: number | null;
  measuredRequests: number;
  requests: number;
}) {
  return (
    <Td
      className={cx(
        "text-right font-mono text-xs tabular-nums",
        marginBps === null
          ? "text-fg-subtle"
          : marginBps < 0
            ? "text-danger"
            : marginBps < THIN_MARGIN_BPS
              ? "text-warn"
              : "text-fg",
      )}
    >
      {marginBps === null ? "—" : formatBps(marginBps)}
      {/* The subset the margin was computed on, whenever it is not the whole
          row. A confident-looking percentage over four of nine hundred
          requests has to say so on the same line. */}
      {marginBps !== null && measuredRequests < requests && (
        <span className="ml-1 text-[11px] text-fg-subtle">
          /{formatNumber(measuredRequests)}
        </span>
      )}
    </Td>
  );
}

function Figure({
  label,
  value,
  detail,
  note,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  note: string;
  tone: "neutral" | "good" | "bad" | "warn";
}) {
  return (
    <div className="bg-surface px-4 py-4">
      <p className="micro">{label}</p>
      <p
        className={cx(
          "mt-1.5 font-mono text-2xl tracking-[-0.02em] tabular-nums",
          tone === "bad" && "text-danger",
          tone === "warn" && "text-warn",
          tone === "good" && "text-mint",
          tone === "neutral" && "text-fg",
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-fg-muted">{detail}</p>
      <p className="mt-1.5 text-[11px] leading-5 text-fg-subtle">{note}</p>
    </div>
  );
}

/**
 * Below this, a margin is thin enough to be worth colouring. Matches the model
 * form's own threshold, so a model reading amber there does not read healthy
 * here.
 */
const THIN_MARGIN_BPS = 500;

/** The console's margin colour scale, in one place. */
function marginTone(bps: number | null): "neutral" | "good" | "bad" | "warn" {
  if (bps === null) return "neutral";
  if (bps < 0) return "bad";
  return bps < THIN_MARGIN_BPS ? "warn" : "good";
}

/**
 * Basis points as a percentage, signed.
 *
 * Signed always, including the plus. A margin column where a loss is the only
 * row carrying a sign makes the sign easy to miss on a screen being skimmed for
 * exactly that.
 */
export function formatBps(bps: number): string {
  const percent = bps / 100;
  const sign = bps > 0 ? "+" : "";
  return `${sign}${percent.toFixed(percent === Math.round(percent) ? 0 : 1)}%`;
}

/**
 * A whole-number percentage, or null when the denominator is zero.
 *
 * Null rather than 0 throughout this page, because every one of these ratios has
 * a denominator that is legitimately empty — no traffic, no measured requests,
 * no input tokens on an all-video window — and "0%" claims a measurement that
 * was never taken.
 */
export function share(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.round((part / whole) * 100);
}
